import { Fragment } from "react";
import { Key } from "w3c-keys";

import { GeneralLangFactory } from "@bokio/lang";
import { mergeClassNames } from "@bokio/utils/classes";

import { Smiley } from "./Smiley.internals";
import { StepContainer } from "./StepContainer.internals";
import { Grade } from "./utils";

import type { FC, KeyboardEvent } from "react";

import * as styles from "./ratingToast.scss";

interface RatingStepProps {
	canProceed: boolean;
	description: string;
	title: string;
	selectedGrade?: Grade;
	setGrade: (grade: Grade) => void;
	handleClose: () => void;
	handleSubmit: () => Promise<void>;
}

export const RatingStep: FC<RatingStepProps> = ({
	canProceed,
	description,
	title,
	selectedGrade,
	setGrade,
	handleClose,
	handleSubmit,
}) => {
	const lang = GeneralLangFactory();

	const grades: { value: Grade; text: string }[] = [
		{ value: Grade.VeryBad, text: lang.Rating_VeryDifficult },
		{ value: Grade.Bad, text: "\u00A0" },
		{ value: Grade.Okay, text: "\u00A0" },
		{ value: Grade.Good, text: "\u00A0" },
		{ value: Grade.Excellent, text: lang.Rating_VeryEasy },
	];

	const handleKeyDown = (event: KeyboardEvent, grade: Grade) => {
		switch (event.key as Key) {
			case Key.Enter:
			case Key.Space:
				setGrade(grade);
				break;
		}
	};

	return (
		<StepContainer
			title={title}
			description={description}
			canProceed={canProceed}
			handleClose={handleClose}
			handleSubmit={handleSubmit}
		>
			<section className={styles.rating}>
				{grades.map(g => (
					<Fragment key={g.value}>
						<div
							className={mergeClassNames(styles.ratingItem, selectedGrade === g.value && styles.selected)}
							onKeyDown={e => handleKeyDown(e, g.value)}
							onClick={() => setGrade(g.value)}
							tabIndex={0}
							data-testid={`Grade_${g.value}`}
						>
							<Smiley grade={g.value} />
							<span className={mergeClassNames(styles.grade, selectedGrade === g.value && styles.selected)}>
								{/* \u00A0 (non-breaking space) maintains the text area even when empty */}
								{g.text || "\u00A0"}
							</span>
						</div>
					</Fragment>
				))}
			</section>
		</StepContainer>
	);
};
